import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { UnlockModal } from "../components/UnlockModal";
import { useI18n } from "../i18n/LanguageContext";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { bundleSavings } from "../lib/pricing";
import { CheckCircle2, Lock, GraduationCap, PlayCircle, Sparkles, Unlock as UnlockIcon } from "lucide-react";

export default function CourseDetail() {
  const { courseId } = useParams();
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [course, setCourse] = useState(null);
  const [enrollment, setEnrollment] = useState(null);
  const [progress, setProgress] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [unlockModal, setUnlockModal] = useState(null); // { scope, moduleId? }
  const [unlockedIds, setUnlockedIds] = useState(new Set());
  const [savings, setSavings] = useState(null);

  useEffect(() => {
    api.getCourse(courseId).then(setCourse).catch((e) => setError(e.message));
  }, [courseId]);

  useEffect(() => {
    if (!user) return;
    api.myEnrollments().then((rows) => {
      const row = rows.find((r) => r.course.course_id === courseId);
      if (row) {
        setEnrollment(row);
        setProgress(row.progress);
      }
    });
  }, [user, courseId]);

  // Track the unlocked_module_ids for gating badges on the module list
  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        // My enrollments already returned course-level info; we also need
        // the raw unlocked_module_ids per enrollment. Fetch modules & check.
        const rows = await api.myEnrollments();
        const row = rows.find((r) => r.course.course_id === courseId);
        if (row) setEnrollment((prev) => prev || row);
      } catch {
        // silent
      }
    })();
  }, [user, courseId]);

  // Sync unlockedIds from module_progress + explicit refresh after unlock
  const refreshUnlocked = useMemo(
    () => async () => {
      if (!user) return;
      try {
        const rows = await api.myEnrollments();
        const row = rows.find((r) => r.course.course_id === courseId);
        if (row) {
          setEnrollment(row);
          setProgress(row.progress);
        }
        // Fetch unlocked_module_ids indirectly by hitting the course + checking
        // each free module; simpler: pull from the modules on the course doc and
        // treat "unlocked" = free OR module was successfully opened. But the
        // authoritative list is on enrollment. Ask backend directly:
        const orders = await api.listOrders();
        const s = new Set();
        for (const o of orders) {
          if (o.status === "succeeded" && o.course_id === courseId) {
            (o.module_ids || []).forEach((id) => s.add(id));
          }
        }
        setUnlockedIds(s);
      } catch {
        // silent
      }
    },
    [user, courseId]
  );

  useEffect(() => {
    refreshUnlocked();
  }, [refreshUnlocked]);

  // Fetch two quotes to compute bundle savings microcopy — no hardcoded prices.
  useEffect(() => {
    if (!user || !course || !enrollment) return;
    (async () => {
      try {
        // pick a still-locked paid module for single quote
        const paidLocked = course.modules.find(
          (m) => m.type === "paid" && !unlockedIds.has(m.module_id)
        );
        if (!paidLocked) return;
        const [singleQ, fullQ] = await Promise.all([
          api.unlockQuote({ course_id: courseId, scope: "single_module", module_id: paidLocked.module_id }),
          api.unlockQuote({ course_id: courseId, scope: "full_course" }),
        ]);
        const s = bundleSavings({
          unitDisplay: singleQ.amount_display,
          bundleDisplay: fullQ.amount_display,
          paidCount: fullQ.module_ids.length,
        });
        setSavings(s);
      } catch {
        setSavings(null);
      }
    })();
  }, [user, course, enrollment, unlockedIds, courseId]);

  const enrolled = !!enrollment;
  const firstFreeId = useMemo(
    () => (course?.modules || []).find((m) => m.type === "free")?.module_id,
    [course]
  );

  async function onEnroll() {
    if (!user) {
      navigate(`/login?next=${encodeURIComponent(`/courses/${courseId}`)}`);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const enr = await api.enroll(courseId);
      const fresh = await api.myEnrollments();
      const row = fresh.find((r) => r.course.course_id === courseId);
      setEnrollment(row || { ...enr, course, progress: null });
      setProgress(row?.progress || null);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#FDFBF7]">
        <Header />
        <div className="mx-auto max-w-3xl px-6 md:px-10 py-24 text-center" data-testid="course-error">
          <h1 className="text-3xl font-black">{error}</h1>
          <Link to="/courses" className="mt-6 inline-block font-bold text-[#4361EE]">← {t("moduleViewer.lockedCtaCourse")}</Link>
        </div>
      </div>
    );
  }

  if (!course) {
    return (
      <div className="min-h-screen bg-[#FDFBF7]">
        <Header />
        <div className="mx-auto max-w-3xl px-6 py-24">
          <div className="h-64 rounded-3xl bg-white animate-pulse border-2 border-black/5" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFBF7]" data-testid="course-detail-page">
      <Header />

      <section className="relative overflow-hidden">
        <div className="absolute inset-0 opacity-25 blur-3xl" style={{ background: course.cover_gradient }} />
        <div className="relative mx-auto max-w-6xl px-6 md:px-10 py-14 md:py-20 grid md:grid-cols-3 gap-10">
          <div className="md:col-span-2">
            <div className="inline-flex items-center gap-2 rounded-full bg-white border-2 border-black/5 px-3 py-1 text-xs font-black uppercase tracking-wider">
              {course.band} · {course.big_idea}
            </div>
            <h1
              className="mt-4 text-4xl md:text-5xl font-black tracking-tight"
              data-testid="course-detail-title"
            >
              {course.title[lang] || course.title.en}
            </h1>
            <p className="mt-4 text-lg text-black/75 leading-relaxed max-w-xl">
              {course.description[lang] || course.description.en}
            </p>

            <div className="mt-6 flex flex-wrap items-center gap-4 text-sm font-bold text-black/70">
              <span>{course.module_count} {t("courses.cardModules")}</span>
              <span>·</span>
              <span>{t("courses.difficulty")}: {"★".repeat(course.difficulty)}{"☆".repeat(5 - course.difficulty)}</span>
              <span>·</span>
              <span className="inline-flex items-center gap-1"><Sparkles className="h-3.5 w-3.5" /> {course.free_module_count} {t("courseDetail.free")}</span>
            </div>

            {enrolled ? (
              <div className="mt-8 rounded-3xl bg-white border-2 border-black/5 p-5 max-w-md" data-testid="course-progress-card">
                <div className="flex items-center justify-between text-sm font-bold text-black/70">
                  <span>{t("courseDetail.progress")}</span>
                  <span>{progress?.percent_complete ?? 0}%</span>
                </div>
                <div className="mt-2 h-2 rounded-full bg-black/5 overflow-hidden">
                  <div
                    className="h-full rounded-full"
                    style={{ width: `${progress?.percent_complete ?? 0}%`, background: course.cover_gradient }}
                  />
                </div>
                {progress?.next_module_id && (
                  <Link
                    to={`/courses/${course.course_id}/modules/${progress.next_module_id}`}
                    className="mt-4 inline-flex items-center gap-2 rounded-full bg-[#4361EE] text-white font-bold px-5 py-3 shadow-[0_3px_0_#2A3EB1] hover:translate-y-[1px] hover:shadow-[0_2px_0_#2A3EB1] transition text-sm"
                    data-testid="course-continue-btn"
                  >
                    <PlayCircle className="h-4 w-4" /> {t("courseDetail.openCta")}
                  </Link>
                )}
              </div>
            ) : (
              <div className="mt-8 flex flex-wrap gap-3">
                <button
                  onClick={onEnroll}
                  disabled={busy}
                  className="inline-flex items-center gap-2 rounded-full bg-[#4361EE] hover:bg-[#4361EE]/90 text-white font-bold px-6 py-3.5 shadow-[0_4px_0_#2A3EB1] hover:translate-y-[1px] hover:shadow-[0_3px_0_#2A3EB1] transition"
                  data-testid="course-enroll-btn"
                >
                  {busy ? "…" : t("courseDetail.enrollCta")}
                </button>
                {firstFreeId && (
                  <Link
                    to={`/courses/${course.course_id}/modules/${firstFreeId}`}
                    className="inline-flex items-center gap-2 rounded-full bg-white border-2 border-black/10 hover:border-black/30 font-bold px-6 py-3.5 transition"
                    data-testid="course-startmod1-btn"
                  >
                    {t("courseDetail.startCta")}
                  </Link>
                )}
              </div>
            )}
          </div>

          <div className="hidden md:flex items-center justify-center">
            <div className="relative">
              <div className="absolute inset-0 rounded-[3rem] rotate-3" style={{ background: course.cover_gradient }} />
              <div className="relative rounded-[3rem] bg-white p-10 border-2 border-black/5 shadow-[0_30px_60px_rgba(0,0,0,0.10)] grid place-items-center h-56 w-56">
                <span className="text-8xl">{course.cover_emoji || "✨"}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-4xl px-6 md:px-10 py-10">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <h2 className="text-2xl md:text-3xl font-black">{t("courseDetail.moduleList")}</h2>
          {enrolled && course.paid_module_count > 0 && (
            <div className="flex flex-col items-end gap-1">
              <button
                onClick={() => setUnlockModal({ scope: "full_course" })}
                className="inline-flex items-center gap-2 rounded-full bg-[#1A1A1A] text-white font-bold px-5 py-2.5 text-sm shadow-[0_3px_0_#000] hover:translate-y-[1px] hover:shadow-[0_2px_0_#000] transition"
                data-testid="unlock-all-btn"
              >
                <UnlockIcon className="h-4 w-4" /> {t("courseDetail.unlockAll")} — $29
              </button>
              {savings && savings.saves > 0 && (
                <div
                  className="text-[11px] font-bold text-[#166534] tracking-tight"
                  data-testid="bundle-savings"
                >
                  {t("unlock.savings")} ${savings.saves.toFixed(2)} · {lang === "ar" ? savings.formatAr : savings.formatEn}
                </div>
              )}
            </div>
          )}
        </div>
        <ul className="mt-6 space-y-3" data-testid="module-list">
          {course.modules.map((m) => (
            <ModuleRow
              key={m.module_id}
              m={m}
              lang={lang}
              enrolled={enrolled}
              course={course}
              t={t}
              unlocked={unlockedIds.has(m.module_id)}
              onUnlock={() => setUnlockModal({ scope: "single_module", moduleId: m.module_id })}
            />
          ))}
        </ul>
      </section>

      <UnlockModal
        open={!!unlockModal}
        onClose={() => setUnlockModal(null)}
        scope={unlockModal?.scope}
        courseId={courseId}
        moduleId={unlockModal?.moduleId}
        onUnlocked={async () => {
          await refreshUnlocked();
        }}
      />

      <Footer />
    </div>
  );
}

function ModuleRow({ m, lang, enrolled, course, t, unlocked, onUnlock }) {
  const isFree = m.type === "free";
  const isExam = m.type === "exam";
  const isPaid = m.type === "paid";
  const isPaidUnlocked = isPaid && unlocked;

  const badge =
    isFree ? (
      <span className="rounded-full bg-[#DCFCE7] text-[#166534] font-black px-2.5 py-1 text-[10px] uppercase tracking-wider">
        {t("courseDetail.free")}
      </span>
    ) : isPaid ? (
      isPaidUnlocked ? (
        <span className="rounded-full bg-[#DCFCE7] text-[#166534] font-black px-2.5 py-1 text-[10px] uppercase tracking-wider inline-flex items-center gap-1">
          <CheckCircle2 className="h-3 w-3" /> {t("courseDetail.unlocked")}
        </span>
      ) : (
        <span className="rounded-full bg-[#FEF3C7] text-[#7A4B00] font-black px-2.5 py-1 text-[10px] uppercase tracking-wider inline-flex items-center gap-1">
          <Lock className="h-3 w-3" /> {t("courseDetail.paid")}
        </span>
      )
    ) : (
      <span className="rounded-full bg-[#EDE9FE] text-[#4C1D95] font-black px-2.5 py-1 text-[10px] uppercase tracking-wider inline-flex items-center gap-1">
        <GraduationCap className="h-3 w-3" /> {t("courseDetail.exam")}
      </span>
    );

  const canOpen = enrolled && (isFree || isPaidUnlocked);
  const isExamOpenable = isExam && enrolled;
  const body = (
    <div
      className={`flex items-center gap-4 rounded-2xl border-2 p-4 md:p-5 transition ${
        (canOpen || isExamOpenable)
          ? "bg-white border-black/5 hover:border-[#4361EE]/40 hover:shadow-[0_10px_30px_rgba(0,0,0,0.06)]"
          : "bg-white/70 border-black/5"
      }`}
      data-testid={`module-row-${m.order}`}
    >
      <div className="h-10 w-10 rounded-full grid place-items-center font-black text-sm bg-black/5">
        {m.order}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          {badge}
          <span className="text-xs text-black/50 font-semibold">{m.estimated_minutes} min</span>
        </div>
        <div className="mt-1 font-extrabold text-[#1A1A1A] truncate">{m.title[lang] || m.title.en}</div>
        <div className="text-sm text-black/60 truncate">{m.summary[lang] || m.summary.en}</div>
      </div>
      <div className="hidden md:flex items-center gap-2 text-end">
        {isFree ? (
          enrolled ? (
            <span className="text-sm font-bold text-[#166534] inline-flex items-center gap-1">
              <PlayCircle className="h-4 w-4" /> {t("courses.cardOpen")}
            </span>
          ) : (
            <span className="text-sm font-bold text-black/50">{t("courseDetail.enrollCta")}</span>
          )
        ) : isPaid ? (
          isPaidUnlocked ? (
            <span className="text-sm font-bold text-[#166534] inline-flex items-center gap-1">
              <PlayCircle className="h-4 w-4" /> {t("courses.cardOpen")}
            </span>
          ) : enrolled ? (
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onUnlock?.();
              }}
              className="rounded-full bg-[#1A1A1A] text-white font-bold px-4 py-2 text-xs shadow-[0_3px_0_#000] hover:translate-y-[1px] hover:shadow-[0_2px_0_#000] transition inline-flex items-center gap-1"
              data-testid={`unlock-module-btn-${m.order}`}
            >
              <UnlockIcon className="h-3.5 w-3.5" /> {t("courseDetail.unlockThis")}
            </button>
          ) : (
            <span className="text-sm font-bold text-black/50 inline-flex items-center gap-1">
              <Lock className="h-4 w-4" /> {t("courseDetail.unlockSoon")}
            </span>
          )
        ) : (
          <span className="text-sm font-bold text-[#4C1D95] inline-flex items-center gap-1">
            <GraduationCap className="h-4 w-4" /> {t("exam.startCta")}
          </span>
        )}
      </div>
    </div>
  );

  if (isExamOpenable) {
    return (
      <li>
        <Link to={`/courses/${course.course_id}/exam`} data-testid={`exam-launch-${m.order}`}>{body}</Link>
      </li>
    );
  }
  if (canOpen) {
    return (
      <li>
        <Link to={`/courses/${course.course_id}/modules/${m.module_id}`}>{body}</Link>
      </li>
    );
  }
  return <li>{body}</li>;
}
